import React, { createContext, useContext, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { db } from '../data/store';
import { platform, hasPlatform } from '../platform';
import { NOTE_TEMPLATES } from '../data/noteTemplates';
import { collection, doc, setDoc, getDoc, getDocs, onSnapshot, query, orderBy, newId, writeBatch, MAX_BATCH_OPS } from '@battleops/localstore';
import {
  localDateStr, SYSTEM_NOTEBOOKS, INBOX_ID, notebookForKind, textToHtml, bodyMeta, linksPatch, noteLinks,
  migrateNote, isTrashed, trashExpired, TEMPLATES_ID, newInkMeta, inkChunkId, inkChunkPage, strokeCount,
  personalNotebookId, personalNotebookDoc, placeNote, isLegacyNotebook, LEGACY_NOTEBOOK_TAG, KIND_TAG,
  notebookSections, sectionsAdd, sectionsRename, sectionsMove, sectionsRemove,
  createKeyring, openKeyring, rewrapKeyring, lockedMeta, sealBodyData, bodyHtml, displayNote, sealText, openText, isSealed, b64encode, b64decode,
  htmlToText, LOCKED_PREVIEW_MAX, bodySid, bodyLineage,
  notebookAccess, notebookAccessPatch, noteAccessPatch, noteAccessOverride, resolveNoteAccess, sameAccess,
} from '@battleops/shared';
import { useSession } from './SessionContext';
import { getSync } from '../session/boot';

// Notes + notebooks (docs/plans/notes.md). A note is `notes/{id}` (metadata + plain-text copy in
// `content`, which the Daily Log / Week / search read) and `notes/{id}/chunks/body` ({ html, rev },
// read from disk only when the note is opened). "Delete" moves to Trash for 30 days.
const NotesCtx = createContext();
const NOTES = 'notes';
const NOTEBOOKS = 'notebooks';
const bodyRef = (id) => doc(db, `${NOTES}/${id}/chunks/body`);
const chunksOf = (id) => collection(db, `${NOTES}/${id}/chunks`);
const MAX_REVISIONS = 20;
const LOCK_DOC = () => doc(db, 'meta', 'notesLock');      // the locked-notes keyring (shared/lock.js)
const RELOCK_MS = 10 * 60 * 1000;                          // locked notes close again after 10 idle minutes
const secureKeyName = (uid) => `ops.notesKey.${uid}`;
const secureStore = () => (hasPlatform() ? platform().secure || null : null);

/** Thrown when a locked note is opened or saved while the notes are locked. */
export class NoteLockedError extends Error { constructor() { super('This note is locked — unlock your notes first.'); this.locked = true; } }

/** Re-save notes' body chunks as they are, so the server re-files them after the note's privacy changed (a chunk is
 *  judged by its note — docs/SYNC.md §3.7: others get it deleted, or back). */
async function touchBodies(ids) {
  for (let i = 0; i < ids.length; i += 50) {
    const batch = writeBatch(db); let n = 0;
    for (const id of ids.slice(i, i + 50)) {
      const snap = await getDoc(bodyRef(id));
      if (snap.exists()) { batch.set(bodyRef(id), { ...snap.data(), touchedAt: Date.now() }); n++; }
    }
    if (n) await batch.commit();
  }
}

export function NoteProvider({ children }) {
  const { identity } = useSession() || {};          // no session (tests, tools) → no personal notebook, Inbox as before
  const uid = identity?.uid || null;
  const personalId = uid ? personalNotebookId(uid) : null;
  const [all, setAll] = useState([]);
  const [rawNotebooks, setRawNotebooks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [booksLoaded, setBooksLoaded] = useState(false);
  const allRef = useRef(all); allRef.current = all;
  const booksRef = useRef(rawNotebooks); booksRef.current = rawNotebooks;
  const placeRef = useRef(null);

  // ---- locked notes: the keyring (synced, useless without the passphrase) and the key (memory only) -------------
  const [keyring, setKeyring] = useState(null);
  const [lockKey, setLockKey] = useState(null);
  const [remembered, setRemembered] = useState(false);
  const keyRef = useRef(null); keyRef.current = lockKey;
  const rememberedRef = useRef(false); rememberedRef.current = remembered;
  const lastUse = useRef(0);
  useEffect(() => onSnapshot(LOCK_DOC(), (snap) => setKeyring(snap.exists() ? snap.data() : null), () => setKeyring(null)), []);
  useEffect(() => {        // "remember on this phone": the key waits in the keystore
    const sec = secureStore();
    if (!uid || !sec) return;
    sec.get(secureKeyName(uid)).then((v) => { if (v) { setLockKey(b64decode(v)); setRemembered(true); } }).catch(() => {});
  }, [uid]);
  useEffect(() => {        // idle → locked again; on the phone, also coming back after a minute or more away
    const t = setInterval(() => { if (keyRef.current && !rememberedRef.current && Date.now() - lastUse.current > RELOCK_MS) setLockKey(null); }, 30000);
    const app = hasPlatform() && platform().os !== 'web' ? platform().appState : null;
    let awayAt = 0;         // not at the moment it leaves: the editor's save-on-background still needs the key
    const unsub = app?.subscribe?.((st) => {
      if (st === 'background') awayAt = Date.now();
      else if (st === 'active' && awayAt && Date.now() - awayAt > 60000 && !rememberedRef.current) setLockKey(null);
      if (st === 'active') awayAt = 0;
    });
    return () => { clearInterval(t); unsub?.(); };
  }, []);
  const needKey = () => { if (!keyRef.current) throw new NoteLockedError(); lastUse.current = Date.now(); return keyRef.current; };
  const previewed = useRef(new Set());
  // Locked notes from before previews were sealed (or imported without one): once the key is here, seal their preview
  // from the body so lists can show it while unlocked. Once per note, quietly.
  useEffect(() => {
    if (!lockKey) return;
    const todo = all.filter((n) => n.locked && !n.sealedPreview && !n.deletedAt && !previewed.current.has(n.id)).slice(0, 50);
    if (!todo.length) return;
    todo.forEach((n) => previewed.current.add(n.id));
    (async () => {
      const batch = writeBatch(db); let n = 0;
      for (const note of todo) {
        const snap = await getDoc(bodyRef(note.id));
        if (!snap.exists() || !isSealed(snap.data().sealed)) continue;
        batch.set(doc(db, NOTES, note.id), { sealedPreview: sealText(lockKey, htmlToText(openText(lockKey, snap.data().sealed)).slice(0, LOCKED_PREVIEW_MAX)) }, { merge: true });
        n++;
      }
      if (n) await batch.commit();
    })().catch((e) => console.error('locked previews failed:', e));
  }, [lockKey, all]);

  useEffect(() => {
    const unsubNotes = onSnapshot(query(collection(db, NOTES), orderBy('updatedAt', 'desc')), (snap) => {
      setAll(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoaded(true);
    }, () => { setAll([]); setLoaded(true); });
    const unsubBooks = onSnapshot(collection(db, NOTEBOOKS), (snap) => {
      setRawNotebooks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setBooksLoaded(true);
    }, () => { setRawNotebooks([]); setBooksLoaded(true); });
    return () => { unsubNotes(); unsubBooks(); };
  }, []);

  // Once per launch after load: create the system notebooks, move pre-notebook notes into the new
  // shape (body chunk, notebook, links), and empty Trash of anything older than 30 days.
  const housekept = useRef(false);
  useEffect(() => {
    if (!loaded || !booksLoaded || housekept.current) return;
    housekept.current = true;
    (async () => {
      const ops = [];
      // Only Templates is seeded now; the other old defaults became tags (docs/SYNC.md §3.7).
      const tplDef = SYSTEM_NOTEBOOKS.find((n) => n.id === TEMPLATES_ID);
      if (tplDef && !rawNotebooks.some((n) => n.id === TEMPLATES_ID)) ops.push((b) => b.set(doc(db, NOTEBOOKS, TEMPLATES_ID), { ...tplDef, parentId: null, archivedAt: null, createdAt: Date.now() }));
      if (personalId && !rawNotebooks.some((n) => n.id === personalId)) ops.push((b) => b.set(doc(db, NOTEBOOKS, personalId), { ...personalNotebookDoc(uid, identity?.name), createdAt: Date.now() }));
      // My notes still in an old default (Inbox, Daily logs, Week reviews — or none) move to my notebook with its tag;
      // every launch, so a phone on an older build that still files into Inbox is tidied up too.
      const mineHere = (n) => n.kind !== 'template' && (!n.ownerUid || n.ownerUid === uid) && isLegacyNotebook(n.notebookId);
      const moving = personalId ? all.filter(mineHere) : [];
      for (const n of moving) {
        const tag = LEGACY_NOTEBOOK_TAG[n.notebookId] || KIND_TAG[n.kind] || null;
        const tags = tag && !(n.tags || []).includes(tag) ? [...(n.tags || []), tag] : (n.tags || []);
        ops.push((b) => b.set(doc(db, NOTES, n.id), { notebookId: personalId, private: true, tags }, { merge: true }));
      }
      if (moving.length) await touchBodies(moving.map((n) => n.id));
      for (const legacy of Object.keys(LEGACY_NOTEBOOK_TAG)) {
        const nb = rawNotebooks.find((x) => x.id === legacy);
        if (nb && !nb.archivedAt && personalId) ops.push((b) => b.set(doc(db, NOTEBOOKS, legacy), { archivedAt: Date.now() }, { merge: true }));
      }
      const expired = [];
      for (const n of all) {
        if (trashExpired(n)) { expired.push(n.id); continue; }
        const m = migrateNote(n);
        if (m) ops.push((b) => b.update(doc(db, NOTES, n.id), m.patch), (b) => b.set(bodyRef(n.id), { ...m.body, savedAt: Date.now() }));
      }
      for (let i = 0; i < ops.length; i += MAX_BATCH_OPS) {
        const batch = writeBatch(db);
        ops.slice(i, i + MAX_BATCH_OPS).forEach((op) => op(batch));
        await batch.commit();
      }
      if (expired.length) await deleteForever(expired);
      // Starter templates, once per org (the Templates notebook remembers it seeded them).
      const tplBook = rawNotebooks.find((n) => n.id === TEMPLATES_ID);
      if (!tplBook?.seeded) {
        if (!all.some((n) => n.kind === 'template')) NOTE_TEMPLATES.forEach((t) => addNote({ ...t, kind: 'template', notebookId: TEMPLATES_ID }));
        await setDoc(doc(db, NOTEBOOKS, TEMPLATES_ID), { ...SYSTEM_NOTEBOOKS.find((n) => n.id === TEMPLATES_ID), parentId: null, archivedAt: null, seeded: true }, { merge: true });
      }
    })().catch((e) => console.error('notes housekeeping failed:', e));
  }, [loaded, booksLoaded]);

  // Lists see a locked note's real title only while the notes are unlocked ("Locked note" otherwise).
  const shown = useMemo(() => (all.some((n) => n.locked) ? all.map((n) => displayNote(n, lockKey)) : all), [all, lockKey]);
  const notes = useMemo(() => shown.filter((n) => !isTrashed(n) && n.kind !== 'template'), [shown]);
  const trash = useMemo(() => shown.filter(isTrashed), [shown]);
  const notebooks = useMemo(() => {
    const byId = new Map(rawNotebooks.map((n) => [n.id, n]));
    const tpl = SYSTEM_NOTEBOOKS.find((n) => n.id === TEMPLATES_ID);
    if (tpl && !byId.has(TEMPLATES_ID)) byId.set(TEMPLATES_ID, tpl);                                  // until housekeeping writes them
    if (personalId && !byId.has(personalId)) byId.set(personalId, personalNotebookDoc(uid, identity?.name));
    return [...byId.values()];
  }, [rawNotebooks, personalId, uid, identity?.name]);
  // Where a note really goes (an old default → my notebook + its tag) and whether it's private.
  placeRef.current = (note) => placeNote(note, { notebooks, personalId });

  // ---- notes -------------------------------------------------------------------------------

  /** New note. Accepts plain `content` (Daily Log, Week) or `html`. Returns the id. */
  const addNote = useCallback((note = {}) => {
    const id = newId();
    const now = Date.now();
    const html = note.html ?? textToHtml(note.content || '');
    const kind = note.kind || 'note';
    const k = note.locked ? needKey() : null;
    const meta = {
      id,
      title: note.title || '',
      icon: note.icon || null,
      ...linksPatch(noteLinks(note)),
      ...(kind === 'template' ? { notebookId: note.notebookId || TEMPLATES_ID, tags: note.tags || [] } : placeRef.current({ notebookId: note.notebookId || notebookForKind(kind), kind, tags: note.tags })),
      // The day this note belongs to (local). Defaults to today; the Daily Log files notes by it.
      date: note.date || localDateStr(now),
      ...(note.sectionId ? { sectionId: note.sectionId } : {}),
      kind,
      pinned: !!note.pinned,
      deletedAt: null,
      bodyRev: 1,
      ...(k ? lockedMeta(k, { title: note.title || '', html }) : bodyMeta(html)),
      createdAt: now,
      updatedAt: now,
    };
    setAll((prev) => [meta, ...prev]);
    const batch = writeBatch(db);
    batch.set(doc(db, NOTES, id), meta);
    batch.set(bodyRef(id), k ? sealBodyData(k, { html, rev: 1, savedAt: now }) : { html, rev: 1, savedAt: now });
    batch.commit().catch((e) => console.error('addNote failed:', e));
    return id;
  }, []);

  /** Metadata only (title, date, notebookId, links, pinned, tags, icon, deletedAt). */
  const updateNote = useCallback(({ id, ...patch }) => {
    if (!id) return;
    const next = { ...patch, updatedAt: Date.now() };
    if ('notebookId' in patch && patch.kind !== 'template') {
      const cur = allRef.current.find((n) => n.id === id);
      const placed = placeRef.current({ notebookId: patch.notebookId, kind: cur?.kind, tags: patch.tags ?? cur?.tags });
      Object.assign(next, placed);
      const own = noteAccessOverride(cur);                // its own access (§3.10) decides `private`, not the notebook's
      if (own) next.private = own.mode === 'private';
      // A section belongs to its notebook: a move elsewhere lands unsectioned unless one is given.
      if (!('sectionId' in patch) && cur && placed.notebookId !== cur.notebookId) next.sectionId = null;
      if (!!cur?.private !== next.private || (!own && cur && placed.notebookId !== cur.notebookId)) touchBodies([id]).catch(() => {});
    }
    if ('links' in patch) Object.assign(next, linksPatch(patch.links));
    if ('title' in patch && allRef.current.find((n) => n.id === id)?.locked) {      // a locked note's title is sealed
      if (keyRef.current) Object.assign(next, { title: '', sealedTitle: sealText(needKey(), patch.title ?? '') }); else delete next.title;
    }
    setAll((prev) => prev.map((n) => (n.id === id ? { ...n, ...next } : n)));
    // coalesce: unsent edits to the same note fold into one queued write (docs/SYNC.md, Store.write)
    setDoc(doc(db, NOTES, id), next, { merge: true, coalesce: true }).catch((e) => console.error('updateNote failed:', e));
  }, []);

  /** The note's body html — from its chunk, or built from `content` for a note not migrated yet. */
  const loadBody = useCallback(async (id) => {
    const snap = await getDoc(bodyRef(id));
    if (snap.exists()) {
      const d = snap.data();
      if (isSealed(d.sealed)) return { html: openText(needKey(), d.sealed), rev: d.rev || 1, ...bodyLineage(d), locked: true };
      return { html: d.html || '', rev: d.rev || 1, ...bodyLineage(d) };
    }
    const n = allRef.current.find((x) => x.id === id);
    return { html: textToHtml(n?.content || ''), rev: 0, sid: null, base: null };
  }, []);

  /**
   * Save the body; the metadata's plain-text copy, word count and rev follow in the same batch.
   * `baseRev` = the rev this editor opened / last saved. If the stored body has moved past it (another
   * device saved in between) and differs, that version is kept as a conflict revision — never lost.
   * `snapshot` also files this version under History (the editor does this once per editing session).
   */
  // Lineage (docs/SYNC.md §3.8): each save writes `sid` (its own id — the caller's, or a new one) and `base` = `baseSid`,
  // the body the editor agreed on. "Moved on" = the stored body's sid isn't that one (rev alone can't tell: two devices
  // saving from one body both write rev N+1), or its rev is past baseRev.
  // mergeOnConflict: when the body moved on, write nothing and return { conflict: { html, rev, sid, base } } so the
  // caller (an open editor) merges it into what's typed and saves again — instead of last-writer-wins.
  const saveBody = useCallback(async (id, rawHtml, extra = {}, { baseRev = null, baseSid, sid = null, snapshot = false, mergeOnConflict = false } = {}) => {
    let html = rawHtml;
    if (/<img[^>]+src="(file|content):/.test(html)) {
      const up = await (platform().media?.uploadedForNote?.(id) ?? Promise.resolve({})).catch(() => ({}));
      for (const [local, url] of Object.entries(up)) html = html.split(local).join(url);
    }
    const k = allRef.current.find((n) => n.id === id)?.locked ? needKey() : null;       // a locked note saves sealed
    const seal = (data) => (k ? sealBodyData(k, data) : data);
    const cur = await getDoc(bodyRef(id));
    const curRev = cur.exists() ? cur.data().rev || 0 : 0;
    const curHtml = cur.exists() ? bodyHtml(cur.data(), k) : '';
    const curLine = bodyLineage(cur.exists() ? cur.data() : null);
    const moved = cur.exists() && curHtml !== html
      && ((baseSid !== undefined && curLine.sid !== (baseSid || null)) || (baseRev != null && curRev > baseRev));
    if (mergeOnConflict && moved) return { conflict: { html: curHtml, rev: curRev, ...curLine } };
    const saveSid = sid || bodySid();
    const rev = curRev + 1;
    const now = Date.now();
    const { title: newTitle, ...rest } = extra;
    const meta = k
      ? { ...rest, ...('links' in rest ? linksPatch(rest.links) : {}), ...lockedMeta(k, { html, ...(newTitle !== undefined ? { title: newTitle } : {}) }), bodyRev: rev, updatedAt: now }
      : { ...extra, ...('links' in extra ? linksPatch(extra.links) : {}), ...bodyMeta(html), bodyRev: rev, updatedAt: now };
    setAll((prev) => prev.map((n) => (n.id === id ? { ...n, ...meta } : n)));
    const batch = writeBatch(db);
    const conflict = moved;
    if (conflict) batch.set(doc(db, `${NOTES}/${id}/chunks/rev-${now}-c`), seal({ html: curHtml, rev: curRev, savedAt: cur.data().savedAt || now, conflict: true }));
    if (snapshot) batch.set(doc(db, `${NOTES}/${id}/chunks/rev-${now}`), seal({ html, rev, savedAt: now }));
    batch.set(bodyRef(id), seal({ html, rev, savedAt: now, sid: saveSid, base: (baseSid === undefined ? curLine.sid : baseSid) || null }));
    batch.update(doc(db, NOTES, id), meta);
    await batch.commit({ coalesce: !conflict && !snapshot });
    if (snapshot || conflict) pruneRevisions(id).catch(() => {});
    return rev;
  }, []);

  /** Saved versions, newest first: [{ id, html, rev, savedAt, conflict }]. */
  const listRevisions = useCallback(async (id) => {
    const snap = await getDocs(chunksOf(id));
    return snap.docs.filter((d) => d.id.startsWith('rev-')).map((d) => ({ id: d.id, ...d.data(), html: bodyHtml(d.data(), keyRef.current) ?? '' }))
      .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  }, []);
  async function pruneRevisions(id) {
    const revs = await listRevisions(id);
    if (revs.length <= MAX_REVISIONS) return;
    const batch = writeBatch(db);
    revs.slice(MAX_REVISIONS).forEach((r) => batch.delete(doc(db, `${NOTES}/${id}/chunks/${r.id}`)));
    await batch.commit();
  }
  /** File the current body under History (the editor does this when you leave a note you changed). */
  const snapshotBody = useCallback(async (id, html) => {
    const now = Date.now();
    const n = allRef.current.find((x) => x.id === id);
    const data = { html, rev: n?.bodyRev || 0, savedAt: now };
    await setDoc(doc(db, `${NOTES}/${id}/chunks/rev-${now}`), n?.locked ? sealBodyData(needKey(), data) : data);
    pruneRevisions(id).catch(() => {});
  }, []);

  /** Keep a body saved elsewhere as a conflict copy in History (an open editor kept its own text in a block both
   *  edited — docs/SYNC.md §3.8). Returns the copy's id. */
  const keepConflictCopy = useCallback(async (id, html, rev = 0) => {
    const now = Date.now();
    const n = allRef.current.find((x) => x.id === id);
    const data = { html, rev, savedAt: now, conflict: true };
    const rid = `rev-${now}-c`;
    await setDoc(doc(db, `${NOTES}/${id}/chunks/${rid}`), n?.locked ? sealBodyData(needKey(), data) : data);
    pruneRevisions(id).catch(() => {});
    return rid;
  }, []);

  /** An open note's body as it changes (local saves and pulls): cb({ html, rev, sid, base }) — html null while a
   *  locked note's key is away. Returns unsubscribe. */
  const watchBody = useCallback((id, cb) => onSnapshot(bodyRef(id), (snap) => {
    if (!snap.exists()) return;
    const d = snap.data();
    cb({ html: bodyHtml(d, keyRef.current), rev: d.rev || 0, ...bodyLineage(d) });
  }, () => {}), []);

  /** Push now (an open note after a typing pause / on blur): push + pull, one round trip — coalesced by the engine.
   *  `pushOnly` (going to the background): push what's waiting, no pull. */
  const pushNotes = useCallback(({ pushOnly = false } = {}) => {
    const sync = getSync();
    if (!sync) return Promise.resolve(null);
    return (pushOnly ? sync.flush(0) : sync.syncNow()).catch(() => null);
  }, []);

  /** Put an old version back; what's there now is filed under History first. */
  const restoreRevision = useCallback(async (id, html, { sid = null } = {}) => {
    const cur = await getDoc(bodyRef(id));
    if (cur.exists() && bodyHtml(cur.data(), keyRef.current) !== html) {      // filed as it is (sealed stays sealed)
      await setDoc(doc(db, `${NOTES}/${id}/chunks/rev-${Date.now()}`), { ...cur.data(), rev: cur.data().rev || 0, savedAt: cur.data().savedAt || Date.now() });
    }
    return saveBody(id, html, {}, { sid });
  }, [saveBody]);

  const deleteNote = useCallback((id) => updateNote({ id, deletedAt: Date.now() }), [updateNote]);   // → Trash
  const restoreNote = useCallback((id) => {
    const n = allRef.current.find((x) => x.id === id);
    const nbOk = !n?.notebookId || rawNotebooks.some((b) => b.id === n.notebookId && !b.archivedAt) || SYSTEM_NOTEBOOKS.some((b) => b.id === n?.notebookId);
    updateNote({ id, deletedAt: null, ...(nbOk ? {} : { notebookId: personalId || INBOX_ID }) });
  }, [updateNote, rawNotebooks]);
  /** Gone for good: the note, its body and its History. */
  const deleteForever = useCallback(async (ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    setAll((prev) => prev.filter((n) => !list.includes(n.id)));
    for (const id of list) {
      try {
        const chunks = (await getDocs(chunksOf(id))).docs.map((d) => d.id);
        const batch = writeBatch(db);
        batch.delete(doc(db, NOTES, id));
        batch.delete(bodyRef(id));
        chunks.filter((c) => c !== 'body').slice(0, MAX_BATCH_OPS - 2).forEach((c) => batch.delete(doc(db, `${NOTES}/${id}/chunks/${c}`)));
        await batch.commit();
      } catch (e) { console.error('deleteForever failed:', e); }
    }
  }, []);
  const togglePin = useCallback((id) => {
    const n = allRef.current.find((x) => x.id === id);
    if (n) updateNote({ id, pinned: !n.pinned });
  }, [updateNote]);

  /** A copy with the same body, notebook and links — "(copy)" on the title, dated today. */
  const duplicateNote = useCallback(async (id) => {
    const n = allRef.current.find((x) => x.id === id);
    if (!n) return null;
    const { html } = await loadBody(id);
    const title = n.locked ? openText(needKey(), n.sealedTitle) : n.title;        // a locked note's copy is locked too
    return addNote({ title: `${title || 'Untitled'} (copy)`, html, notebookId: n.notebookId, links: noteLinks(n), tags: n.tags, icon: n.icon, locked: !!n.locked });
  }, [addNote, loadBody]);

  // ---- notebooks ---------------------------------------------------------------------------

  const addNotebook = useCallback(({ name, icon = '📓', color = '#FB5446', parentId = null, visibility = 'team', access = null }) => {
    const id = newId();
    const nb = { id, name: name.trim() || 'Notebook', icon, color, parentId, ...notebookAccessPatch(access ?? { mode: visibility === 'private' ? 'private' : 'team' }), sortOrder: Date.now(), archivedAt: null, createdAt: Date.now() };
    setRawNotebooks((prev) => [...prev, nb]);
    setDoc(doc(db, NOTEBOOKS, id), nb).catch((e) => console.error('addNotebook failed:', e));
    return id;
  }, []);

  const updateNotebook = useCallback(({ id, ...patch }) => {
    // Access (docs/SYNC.md §3.10): `access` (or the legacy `visibility`) → the clean shape + the §3.7 mirror.
    const accessChange = 'access' in patch || 'visibility' in patch;
    const before = booksRef.current.find((n) => n.id === id);
    if (accessChange) Object.assign(patch, notebookAccessPatch('access' in patch ? patch.access : { mode: patch.visibility === 'private' ? 'private' : 'team' }));
    setRawNotebooks((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    setDoc(doc(db, NOTEBOOKS, id), { ...patch, updatedAt: Date.now() }, { merge: true }).catch((e) => console.error('updateNotebook failed:', e));
    // Its notes follow (all but those with their own access): `private` to match, and each note and its body re-saved,
    // so the server re-files them — whoever just lost access gets them deleted, whoever gained it gets them.
    if (!accessChange || (before && sameAccess(notebookAccess(before), patch.access))) return;
    const priv = patch.access.mode === 'private';
    const inIt = allRef.current.filter((n) => n.notebookId === id && !noteAccessOverride(n));
    if (!inIt.length) return;
    const at = Date.now();
    setAll((prev) => prev.map((n) => (inIt.some((x) => x.id === n.id) ? { ...n, private: priv } : n)));
    (async () => {
      for (let i = 0; i < inIt.length; i += 200) {
        const batch = writeBatch(db);
        inIt.slice(i, i + 200).forEach((n) => batch.set(doc(db, NOTES, n.id), { private: priv, accessAt: at }, { merge: true }));
        await batch.commit();
      }
      await touchBodies(inIt.map((n) => n.id));
    })().catch((e) => console.error('notebook access failed:', e));
  }, []);

  /** One note's own access (§3.10): an access set, or null = the same as its notebook. Re-saves its body too. */
  const setNoteAccess = useCallback((id, access) => {
    const cur = allRef.current.find((n) => n.id === id);
    if (!cur) return;
    const nb = booksRef.current.find((b) => b.id === cur.notebookId) || null;
    const next = { ...noteAccessPatch(access, nb), accessAt: Date.now() };
    setAll((prev) => prev.map((n) => (n.id === id ? { ...n, ...next } : n)));
    setDoc(doc(db, NOTES, id), next, { merge: true }).then(() => touchBodies([id])).catch((e) => console.error('note access failed:', e));
  }, []);

  /** A note's resolved access: { mode, departments, roles, people, inherited, owners }. */
  const noteAccessOf = useCallback((note) => resolveNoteAccess(note, booksRef.current.find((b) => b.id === note?.notebookId) || null), []);

  // ---- locking -------------------------------------------------------------------------------

  /** First time: make the keyring for a passphrase (and unlock). `remember` keeps the key in the phone's keystore. */
  const setupLock = useCallback(async (passphrase, { remember = false } = {}) => {
    if (keyring) throw new Error('Locked notes are already set up — unlock with your passphrase.');
    const { keyring: kr, key } = await createKeyring(passphrase);
    await setDoc(LOCK_DOC(), kr);
    setKeyring(kr); lastUse.current = Date.now(); setLockKey(key);
    if (remember) await rememberKey(key);
  }, [keyring, uid]);
  const rememberKey = async (key) => {
    const sec = secureStore();
    if (!sec || !uid) return;
    await sec.set(secureKeyName(uid), b64encode(key)); setRemembered(true);
  };
  /** Open the notes with the passphrase (throws "wrong passphrase"). */
  const unlockNotes = useCallback(async (passphrase, { remember = false } = {}) => {
    const key = await openKeyring(keyring, passphrase);
    lastUse.current = Date.now(); setLockKey(key);
    if (remember) await rememberKey(key);
  }, [keyring, uid]);
  /** Lock now (and forget the key on this phone). */
  const relockNotes = useCallback(async () => {
    setLockKey(null);
    const sec = secureStore();
    if (sec && uid) await sec.remove(secureKeyName(uid)).catch(() => {});
    setRemembered(false);
  }, [uid]);
  const changePassphrase = useCallback(async (oldP, newP) => {
    const next = await rewrapKeyring(keyring, oldP, newP);
    await setDoc(LOCK_DOC(), next); setKeyring(next);
  }, [keyring]);

  /** Encrypt a note in place: title, body and every saved version. Nothing readable is left to sync. */
  const lockNote = useCallback(async (id) => {
    const k = needKey();
    const n = allRef.current.find((x) => x.id === id);
    if (!n || n.locked) return;
    if (n.format === 'ink' || n.format === 'pdf') throw new Error('Handwritten and PDF notes can’t be locked yet.');
    const { html, rev } = await loadBody(id);
    const revs = await listRevisions(id);
    const now = Date.now();
    const meta = { ...lockedMeta(k, { title: n.title || '', html }), bodyRev: rev + 1, updatedAt: now };
    setAll((prev) => prev.map((x) => (x.id === id ? { ...x, ...meta } : x)));
    const batch = writeBatch(db);
    batch.update(doc(db, NOTES, id), meta);
    batch.set(bodyRef(id), sealBodyData(k, { html, rev: rev + 1, savedAt: now }));
    for (const r of revs.slice(0, MAX_BATCH_OPS - 4)) { const { id: rid, sealed, ...data } = r; batch.set(doc(db, `${NOTES}/${id}/chunks/${rid}`), sealBodyData(k, data)); }
    await batch.commit();
  }, [loadBody, listRevisions]);

  /** Take the lock off for good: title, body and versions back to plain text. */
  const unlockNote = useCallback(async (id) => {
    const k = needKey();
    const n = allRef.current.find((x) => x.id === id);
    if (!n?.locked) return;
    const title = openText(k, n.sealedTitle);
    const { html, rev } = await loadBody(id);
    const revs = await listRevisions(id);
    const now = Date.now();
    const meta = { title, locked: false, sealedTitle: null, ...bodyMeta(html), bodyRev: rev + 1, updatedAt: now };
    setAll((prev) => prev.map((x) => (x.id === id ? { ...x, ...meta } : x)));
    const batch = writeBatch(db);
    batch.update(doc(db, NOTES, id), meta);
    batch.set(bodyRef(id), { html, rev: rev + 1, savedAt: now });
    for (const r of revs.slice(0, MAX_BATCH_OPS - 4)) { const { id: rid, sealed, ...data } = r; batch.set(doc(db, `${NOTES}/${id}/chunks/${rid}`), data); }
    await batch.commit();
  }, [loadBody, listRevisions]);

  /** Lock every note in a notebook (not its notebooks inside). Returns how many. */
  const lockNotebook = useCallback(async (notebookId) => {
    needKey();
    const list = allRef.current.filter((n) => n.notebookId === notebookId && !n.locked && !n.deletedAt && n.format !== 'ink' && n.format !== 'pdf');
    for (const n of list) await lockNote(n.id);
    return list.length;
  }, [lockNote]);

  // ---- sections inside a notebook (shared/notes.js) -----------------------------------------
  // The list lives on the notebook doc; each change writes the whole (small) array.

  const setSections = useCallback((id, fn) => {
    const nb = rawNotebooks.find((n) => n.id === id);
    if (!nb) return null;
    const sections = fn(notebookSections(nb));
    updateNotebook({ id, sections });
    return sections;
  }, [rawNotebooks, updateNotebook]);
  /** New section at the end; returns its id (null if the name is blank or taken). */
  const addSection = useCallback((notebookId, name) => {
    const sid = newId();
    try { setSections(notebookId, (list) => sectionsAdd(list, { id: sid, name })); return sid; } catch { return null; }
  }, [setSections]);
  const renameSection = useCallback((notebookId, sectionId, name) => {
    try { setSections(notebookId, (list) => sectionsRename(list, sectionId, name)); return true; } catch { return false; }
  }, [setSections]);
  const moveSection = useCallback((notebookId, sectionId, dir) => { setSections(notebookId, (list) => sectionsMove(list, sectionId, dir)); }, [setSections]);
  /** Delete a section; its notes stay in the notebook, unsectioned. */
  const deleteSection = useCallback((notebookId, sectionId) => {
    setSections(notebookId, (list) => sectionsRemove(list, sectionId));
    const inIt = allRef.current.filter((n) => n.notebookId === notebookId && n.sectionId === sectionId);
    if (!inIt.length) return;
    setAll((prev) => prev.map((n) => (inIt.some((x) => x.id === n.id) ? { ...n, sectionId: null } : n)));
    (async () => {
      for (let i = 0; i < inIt.length; i += 200) {
        const batch = writeBatch(db);
        inIt.slice(i, i + 200).forEach((n) => batch.set(doc(db, NOTES, n.id), { sectionId: null, updatedAt: Date.now() }, { merge: true }));
        await batch.commit();
      }
    })().catch((e) => console.error('deleteSection failed:', e));
  }, [setSections]);

  // ---- handwritten + PDF notes (shared/ink.js) ----------------------------------------------

  /** New handwritten (format 'ink') or PDF (format 'pdf') note. Returns the id. */
  const addInkNote = useCallback((note = {}) => {
    const id = note.id || newId();
    const now = Date.now();
    const meta = {
      id, title: note.title || '', icon: note.icon || null,
      ...placeRef.current({ notebookId: note.notebookId || INBOX_ID, kind: 'note', tags: note.tags }),
      ...linksPatch(noteLinks(note)),
      date: note.date || localDateStr(now), kind: 'note', pinned: false, deletedAt: null,
      ...newInkMeta(note),
      bodyRev: 0, createdAt: now, updatedAt: now,
    };
    setAll((prev) => [meta, ...prev]);
    setDoc(doc(db, NOTES, id), meta).catch((e) => console.error('addInkNote failed:', e));
    return id;
  }, []);

  /** Every page's strokes: { [page]: json }. */
  const loadInkPages = useCallback(async (id) => {
    const snap = await getDocs(chunksOf(id));
    const out = {};
    for (const d of snap.docs) { const n = inkChunkPage(d.id); if (n != null) out[n] = d.data().strokes || ''; }
    return out;
  }, []);

  /** One page's strokes (coalesced: a burst of strokes is one queued write). */
  const saveInkPage = useCallback(async (id, page, strokes) => {
    const now = Date.now();
    await setDoc(doc(db, `${NOTES}/${id}/chunks/${inkChunkId(page)}`), { page, strokes, count: strokeCount(strokes), savedAt: now }, { coalesce: true });
    setAll((prev) => prev.map((n) => (n.id === id ? { ...n, updatedAt: now } : n)));
    setDoc(doc(db, NOTES, id), { updatedAt: now }, { merge: true, coalesce: true }).catch(() => {});
  }, []);

  /** Delete a notebook: its notes and sub-notebooks move up to its parent (or Inbox). */
  const deleteNotebook = useCallback((id) => {
    const nb = rawNotebooks.find((n) => n.id === id);
    if (!nb || nb.system) return;
    const up = nb.parentId || personalId || INBOX_ID;
    const upPrivate = placeRef.current({ notebookId: up, kind: 'note' }).private;
    const batch = writeBatch(db);
    const moved = [];
    const upBook = rawNotebooks.find((n) => n.id === up);
    const sameSet = !upBook || sameAccess(notebookAccess(nb), notebookAccess(upBook));
    for (const note of allRef.current) {
      if (note.notebookId !== id || moved.length >= MAX_BATCH_OPS - 20) continue;
      const own = noteAccessOverride(note);                       // its own access (§3.10) stays; the rest take the new notebook's
      const priv = own ? own.mode === 'private' : upPrivate;
      batch.update(doc(db, NOTES, note.id), { notebookId: up, private: priv });
      if (!!note.private !== priv || (!own && !sameSet)) moved.push(note.id);
    }
    for (const child of rawNotebooks) if (child.parentId === id) batch.update(doc(db, NOTEBOOKS, child.id), { parentId: nb.parentId || null });
    batch.delete(doc(db, NOTEBOOKS, id));
    batch.commit().then(() => touchBodies(moved)).catch((e) => console.error('deleteNotebook failed:', e));
  }, [rawNotebooks, personalId]);

  return (
    <NotesCtx.Provider value={{
      notes, trash, allNotes: shown, notebooks, loaded, defaultNotebookId: personalId || INBOX_ID, myUid: uid,
      addNote, updateNote, loadBody, saveBody, snapshotBody, listRevisions, restoreRevision, deleteNote, restoreNote, deleteForever, togglePin, duplicateNote,
      keepConflictCopy, watchBody, pushNotes,
      addNotebook, updateNotebook, deleteNotebook, setNoteAccess, noteAccessOf,
      addSection, renameSection, moveSection, deleteSection,
      lockState: { setUp: !!keyring, unlocked: !!lockKey, remembered, canRemember: !!secureStore() },
      setupLock, unlockNotes, relockNotes, changePassphrase, lockNote, unlockNote, lockNotebook,
      openBody: (data) => bodyHtml(data, keyRef.current),
      addInkNote, loadInkPages, saveInkPage,
    }}>
      {children}
    </NotesCtx.Provider>
  );
}

export function useNotes() {
  const context = useContext(NotesCtx);
  if (!context) throw new Error('useNotes must be used within NoteProvider');
  return context;
}
